import React, { useCallback, useEffect, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    Link,
    makeStyles,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
} from '@material-ui/core';
import DragHandleIcon from '@material-ui/icons/DragHandle';
import { Link as RouterLink } from 'react-router-dom';
import { visuallyhidden } from '../../util/styles';
import { EnhancedTableToolbar } from '../EnhancedTable';
import { DraggableList, DragHandle } from '../DraggableList';
import { ProductLabel } from './ProductLabel';
import CheckIcon from '@axes4/react-icons/jsx/NavigateCheck';
import { VisuallyHidden } from '../Utility/VisuallyHidden';
import { License, LicenseUser, Module, Product } from './types';
import { DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';

const useStyles = makeStyles(theme => ({
    th: {
        background: theme.palette.background.paper,
    },
    td: {
        background: theme.palette.background.paper,
    },
    visuallyhidden: visuallyhidden,
}));

export interface LicenseMappingTableLabels {
    licenseUserLabel: string;
    selectAllLabel: string;
    licenseUserRemoveLabel: string;
    dragHandleLabel: string;
    rowActionLabel: string;
    productEnabledLabel: string;
    productDisabledLabel: string;
    getSelectionStatusLabel?: (selectedItems: number, totalItems: number) => React.ReactNode;
}

export interface LicenseMappingTableProps<L extends License, P extends Product, M extends Module, U extends LicenseUser> extends LicenseMappingTableLabels {
    license: L;
    products: P[];
    licenseUsers: U[];
    handleLicenseUserRemove: (licenseUserId: string) => () => void;
    tableHeadActions?: (selectedItems: U[], editable: boolean) => void;
    handleReorder: (items: U[]) => void;
    id: string;
    setModulesForLicenseUser: (licenseUser: U) => (modules: M[]) => void;
    maxHeight?: number;
    orderable?: boolean;
    editable?: boolean;
    placeholder?: string;
}

const forAllTableCells = (cb: (cell: HTMLTableCellElement) => void) =>
        (row: HTMLTableRowElement): void =>
            Array.from(row.cells).forEach(cb);

const fixTableCellWidths = forAllTableCells(cell => cell.style.width = cell.clientWidth + 'px');
const clearTableCellWidths = forAllTableCells(cell => cell.style.width = '');

let dragEndListener;

const handleDragEnd = (row: HTMLTableRowElement) => () => {
    setTimeout(() => {
        clearTableCellWidths(row);
        clearTableCellWidths(row.closest('table').querySelector('thead tr'));
        document.removeEventListener('mouseup', dragEndListener);
        dragEndListener = null;
    }, 600);
};

/**
 * Fixes the width of all table cells on drag start
 */
const handleDragHandleMouseDown = (e: MouseEvent) => {
    const eventTarget = e.target;
    if (!(eventTarget instanceof HTMLElement)) {
        return;
    }
    const row = eventTarget.closest('tr');
    fixTableCellWidths(row);
    fixTableCellWidths(row.closest('table').querySelector('thead tr'));
    dragEndListener = handleDragEnd(row);
    document.addEventListener('mouseup', dragEndListener);
};

export const LicenseMappingTable = <
    U extends LicenseUser,
    P extends Product,
    L extends License,
    M extends Module
>(props: LicenseMappingTableProps<L, P, M, U>): JSX.Element => {
    const classes = useStyles();
    const [ selectedItems, setSelectedItems ] = useState([]);

    const data = props.licenseUsers.map(licenseUser => ({
        id: licenseUser.id,
        label: licenseUser.label,
        activeModules: licenseUser.activeModules,
        link: licenseUser.link,
        onNameChange: licenseUser.onNameChange,
    }));

    useEffect(() => {
        if (!props.editable) {
            setSelectedItems([]);
        }
    }, [ props.editable ]);

    const maxHeight = props.maxHeight || 600;

    const isSelected = item => selectedItems.find(s => s.id === item.id) !== undefined;

    const toggleSelected = useCallback((item: U) => () => {
        setSelectedItems(current => {
            const next = current.filter(i => i.id !== item.id);

            if (next.length !== current.length) {
                return next;
            }

            return [ ...next, item ];
        });
    }, []);

    const rowRenderer = (data: U, dragHandleProps?: DraggableProvidedDragHandleProps) => {
        const productActive = (productId: M): boolean =>
            data.activeModules.indexOf(productId) >= 0;
        const toggleModule = (productId: M) => () => {
            const nextModules: M[] = productActive(productId)
                ? data.activeModules.filter(id => id !== productId) as M[]
                : [ ...data.activeModules, productId ] as M[];
            props.setModulesForLicenseUser(data)(nextModules);
        };

        let userLabel: React.ReactNode;
        if (props.editable && data.onNameChange) {
            userLabel = (
                <TextField
                    value={data.label}
                    onChange={e => data.onNameChange(data.id, e.target.value)}
                    label={props.licenseUserLabel}
                />
            );
        } else if (data.link) {
            userLabel = (
                <Link component={RouterLink} to={data.link}>
                    {data.label}
                </Link>
            );
        } else {
            userLabel = data.label;
        }

        return (
            <>
                {props.editable && (
                    <TableCell padding="checkbox" className={classes.td}>
                        <Checkbox
                            inputProps={{
                                'aria-label': data.label,
                            }}
                            checked={isSelected(data)}
                            onChange={toggleSelected(data)}
                        />
                    </TableCell>
                )}
                <TableCell component="th" id={`${props.id}_${data.id}`} className={classes.td}>
                    {userLabel}
                </TableCell>
                {props.products.map(product => {
                    const active = productActive(product.id as M);
                    return (
                        <TableCell key={product.id} align="center" className={classes.td}>
                            {props.editable ? (
                                <Checkbox
                                    aria-label={product.title}
                                    checked={active}
                                    onChange={toggleModule(product.id as M)}
                                />
                            ) : (
                                active ? (
                                    <>
                                        <CheckIcon size="small"/>
                                        <VisuallyHidden>{props.productEnabledLabel}</VisuallyHidden>
                                    </>
                                ) : (
                                    <VisuallyHidden>{props.productDisabledLabel}</VisuallyHidden>
                                )
                            )}
                        </TableCell>
                    );
                })}
                {props.editable && dragHandleProps && (
                    <TableCell
                        className={classes.td}
                        onMouseDown={handleDragHandleMouseDown}
                    >
                        <DragHandle
                            aria-label={props.dragHandleLabel}
                            dragHandleProps={dragHandleProps}
                            icon={DragHandleIcon}
                        />
                    </TableCell>
                )}
            </>
        );
    };

    const allSelected = selectedItems.length === props.licenseUsers.length;

    const toggleSelection = useCallback(() => {
        setSelectedItems(items => items.length === props.licenseUsers.length ? [] : props.licenseUsers);
    }, [ props.licenseUsers ]);

    const removeSelected = useCallback(selected => () => {
        const licenseUserIdsToRemove = selected.map(licenseUser => licenseUser.id);
        props.handleLicenseUserRemove(licenseUserIdsToRemove)();
        setSelectedItems(selectedItems => selectedItems.filter(licenseUser => licenseUserIdsToRemove.indexOf(licenseUser.id) < 0));
    }, [ props.handleLicenseUserRemove ]);

    const columnCount = props.products.length
        // user
        + 1
        // selection-checkbox
        + (props.editable ? 1 : 0)
        // drag-handle
        + (props.editable && props.orderable ? 1 : 0);

    return (
        <>
            <EnhancedTableToolbar<U>
                allSelected={allSelected}
                /*selectAll={() => setSelectedItems(props.licenseUsers)}*/
                /*selectNone = {() => setSelectedItems([])}*/
                selectedItems={selectedItems}
                totalItemCount={props.licenseUsers.length}
                getSelectionStatusLabel={props.getSelectionStatusLabel}
                getActions={selected => (
                    <>
                        {props.tableHeadActions && props.tableHeadActions(selected, props.editable)}
                        {props.editable && (
                            <Button disabled={selected.length === 0} onClick={removeSelected(selected)} variant="contained">
                                {props.licenseUserRemoveLabel}
                            </Button>
                        )}
                    </>
                )}
                isCardContent
            />
            <Box maxHeight={maxHeight} overflow="auto">
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            {props.editable && (
                                <TableCell padding="checkbox" className={classes.th}>
                                    <Checkbox
                                        checked={selectedItems.length > 0 && allSelected}
                                        onChange={toggleSelection}
                                        indeterminate={!allSelected && selectedItems.length > 0}
                                        inputProps={{
                                            'aria-label': props.selectAllLabel,
                                        }}
                                    />
                                </TableCell>
                            )}
                            <TableCell className={classes.th}>{props.licenseUserLabel}</TableCell>
                            {props.products.map(product => (
                                <TableCell key={product.id} align="center" className={classes.th}>
                                    <ProductLabel
                                        iconUrl={product.icon}
                                        label={product.title}
                                        small
                                    />
                                </TableCell>
                            ))}
                            {props.editable && props.orderable && (
                                <TableCell className={classes.th}>
                                    <span className={classes.visuallyhidden}>{props.rowActionLabel}</span>
                                </TableCell>
                            )}
                        </TableRow>
                    </TableHead>
                    {data.length === 0 && props.placeholder && (
                        <TableBody>
                            <TableRow>
                                <TableCell colSpan={columnCount}>
                                    {props.placeholder}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    )}
                    {data.length > 0 && (props.orderable
                    ? (
                        <DraggableList
                            listComponent={TableBody}
                            listItemComponent={TableRow}
                            data={data as U[]}
                            itemRenderer={rowRenderer}
                            onChange={props.handleReorder}
                            id={props.id}
                        />
                    )
                    : (
                        <TableBody>
                            {data.map((item, i) => (
                                <TableRow key={i}>
                                    {rowRenderer(item as U)}
                                </TableRow>
                            ))}
                        </TableBody>
                    ))}
                </Table>
            </Box>
        </>
    );
};
